import React, { useEffect, useState, useContext } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { 
    Container, Grid, Typography, Box, Paper, Divider, Button, 
    Skeleton, Avatar, Stack, Chip, Dialog, DialogContent, IconButton 
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faArrowLeft, faStore, faMapMarkerAlt, faReceipt, 
    faCreditCard, faTimes, faBoxOpen 
} from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';

// Contexto y API
import UserContext from '../../../navigation/context/userContext';
import { getOrderDetails } from '../../../controllers/customer/OrdersController'; //from '../../../controllers/customer/OrderController';

// Componentes
import MultiOrderPaymentContainer from '../../../components/payments/MultiOrderPaymentContainer';
import CustomBreadcrumbs from '../../../components/breadcrumbs';

export default function ClientOrderDetailPage() {
    const { id } = useParams();
    const history = useHistory();
    const { t } = useTranslation();
    const { userData } = useContext(UserContext);

    const [loading, setLoading] = useState(true);
    const [order, setOrder] = useState(null);
    
    // Modal de Pago
    const [paymentModalOpen, setPaymentModalOpen] = useState(false);

    useEffect(() => {
        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const loadData = async () => {
        setLoading(true);
        const res = await getOrderDetails(id);
        if (res.success) {
            setOrder(res.data);
        } else {
            alert("Error loading order");
            history.push('/orders');
        }
        setLoading(false);
    };

    const handlePaymentSuccess = (success) => {
        if(success) {
            setPaymentModalOpen(false);
            loadData(); // Recargar para ver el estatus actualizado
            alert(t('orders.payment_modal.success_alert'));
        }
    };

    if (loading) return <Container sx={{pt:5}}><Skeleton variant="rectangular" height={400} sx={{borderRadius:3}}/></Container>;
    if (!order) return null;

    const { status, merchant, items, amount, currency, code, formatted_date, shipping_address, can_pay } = order;

    return (
        <Container maxWidth="xl" sx={{ pb: 8 }}>
            <CustomBreadcrumbs 
                returnTo='/orders' 
                crumbs={[
                    { name: t('orders.title'), link: '/orders' }, 
                    { name: `#${code}`, link: '#' }
                ]} 
            />

            {/* --- HEADER: Título y Acciones --- */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={4} mt={2} flexWrap="wrap" gap={2}>
                <Box>
                    <Box display="flex" alignItems="center" gap={2}>
                        <Typography variant="h4" fontWeight="bold">
                            {t('orders.detail.title')} #{code}
                        </Typography>
                        <Chip 
                            label={status.label} 
                            color={status.color} 
                            sx={{ fontWeight: 'bold' }}
                        />
                    </Box>
                    <Typography variant="body2" color="text.secondary" mt={0.5}>
                        {formatted_date}
                    </Typography>
                </Box>

                <Box display="flex" gap={2}>
                    {can_pay && (
                        <Button 
                            variant="contained" 
                            size="large"
                            startIcon={<FontAwesomeIcon icon={faCreditCard} />}
                            onClick={() => setPaymentModalOpen(true)}
                            sx={{ borderRadius: 50, px: 4, fontWeight: 'bold' }}
                        >
                            {t('orders.card.pay_now')}
                        </Button>
                    )}
                </Box>
            </Box>

            <Grid container spacing={4}>
                
                {/* --- COLUMNA IZQUIERDA: ITEMS --- */}
                <Grid item xs={12} md={8}>
                    <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3, overflow: 'hidden' }}>
                        <Box p={3} bgcolor="grey.50" borderBottom="1px solid #eee">
                            <Typography variant="h6" fontWeight="bold">
                                {t('orders.detail.items_title')}
                            </Typography>
                        </Box>

                        <Box p={3}>
                            <Stack spacing={3} divider={<Divider />}>
                                {items.map((item) => (
                                    <Box key={item.id} display="flex" justifyContent="space-between" alignItems="flex-start">
                                        <Box display="flex" gap={2}>
                                            <Avatar 
                                                variant="rounded" 
                                                sx={{ bgcolor: 'primary.lighter', color: 'primary.main' }}
                                            >
                                                <FontAwesomeIcon icon={faBoxOpen} />
                                            </Avatar>
                                            <Box>
                                                <Typography variant="subtitle1" fontWeight="bold">
                                                    {item.name}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    {item.description}
                                                </Typography>
                                                <Typography variant="caption" display="block" mt={0.5}>
                                                    Qty: {item.quantity}
                                                </Typography>
                                            </Box>
                                        </Box>
                                        <Typography variant="subtitle1" fontWeight="bold">
                                            ${item.total.toFixed(2)}
                                        </Typography>
                                    </Box>
                                ))}
                            </Stack>
                        </Box>
                    </Paper>

                    {/* Dirección de Envío (Si existe) */}
                    {shipping_address && (
                        <Paper elevation={0} sx={{ mt: 3, p: 3, border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
                            <Box display="flex" gap={2}>
                                <FontAwesomeIcon icon={faMapMarkerAlt} size="lg" color="#777" style={{marginTop: 5}}/>
                                <Box>
                                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                                        {t('orders.detail.shipping_address')}
                                    </Typography>
                                    <Typography variant="body2">{shipping_address.address}</Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {shipping_address.city}, {shipping_address.zip}
                                    </Typography>
                                    {shipping_address.notes && (
                                        <Typography variant="caption" display="block" mt={1} fontStyle="italic">
                                            "{shipping_address.notes}"
                                        </Typography>
                                    )}
                                </Box>
                            </Box>
                        </Paper>
                    )}
                </Grid>

                {/* --- COLUMNA DERECHA: COMERCIO Y TOTALES --- */}
                <Grid item xs={12} md={4}>
                    
                    {/* Tarjeta del Comercio */}
                    <Paper elevation={0} sx={{ p: 3, mb: 3, border: '1px solid', borderColor: 'divider', borderRadius: 3, textAlign: 'center' }}>
                        <Avatar 
                            src={merchant.logo} 
                            sx={{ width: 80, height: 80, mx: 'auto', mb: 2, border: '1px solid #eee' }} 
                        />
                        <Typography variant="h6" fontWeight="bold">{merchant.name}</Typography>
                        <Button variant="outlined" size="small" sx={{ mt: 2, borderRadius: 50 }} fullWidth>
                            Contact Seller
                        </Button>
                    </Paper>

                    {/* Resumen Financiero */}
                    <Paper elevation={0} sx={{ p: 3, bgcolor: '#fafafa', border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
                        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                            {t('orders.detail.summary')}
                        </Typography>
                        <Divider sx={{ my: 2 }} />
                        
                        <Box display="flex" justifyContent="space-between" mb={1}>
                            <Typography variant="body2" color="text.secondary">Subtotal</Typography>
                            <Typography variant="body2" fontWeight="bold">${amount.toFixed(2)}</Typography>
                        </Box>
                         {/* Si tuvieras tax o delivery separado, iría aquí */}
                        
                        <Divider sx={{ my: 2 }} />
                        
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="h6" fontWeight="bold">Total</Typography>
                            <Typography variant="h5" fontWeight="900" color="primary.main">
                                ${amount.toFixed(2)} <span style={{fontSize: '0.6em'}}>{currency}</span>
                            </Typography>
                        </Box>
                    </Paper>

                </Grid>
            </Grid>

            {/* --- MODAL DE PAGO --- */}
            <Dialog 
                open={paymentModalOpen} 
                onClose={() => setPaymentModalOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <Box display="flex" justifyContent="space-between" alignItems="center" p={2} pb={0}>
                    <Typography variant="h6" fontWeight="bold">
                        {t('orders.payment_modal.title')}
                    </Typography>
                    <IconButton onClick={() => setPaymentModalOpen(false)}>
                        <FontAwesomeIcon icon={faTimes} />
                    </IconButton>
                </Box>
                <DialogContent>
                    <MultiOrderPaymentContainer 
                        paymentData={{
                            total_to_pay: amount, 
                            orders: [order], // Array de 1 elemento
                            reference: null 
                        }}
                        merchantUuid="PLATFORM_UUID" 
                        customer={userData.user}
                        onPaymentSuccess={handlePaymentSuccess}
                    />
                </DialogContent>
            </Dialog>

        </Container>
    );
}